import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, ArrowRight, ArrowLeft, Check, Clock, Loader2, Plus, Trash2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = [
  { value: 0, label: 'Lun', long: 'Lundi' },
  { value: 1, label: 'Mar', long: 'Mardi' },
  { value: 2, label: 'Mer', long: 'Mercredi' },
  { value: 3, label: 'Jeu', long: 'Jeudi' },
  { value: 4, label: 'Ven', long: 'Vendredi' },
  { value: 5, label: 'Sam', long: 'Samedi' },
  { value: 6, label: 'Dim', long: 'Dimanche' },
];

const DURATION_OPTIONS = [
  { value: 30,  label: '30 min' },
  { value: 60,  label: '1 h' },
  { value: 90,  label: '1 h 30' },
  { value: 120, label: '2 h' },
];

const BUFFER_OPTIONS = [
  { value: 0,  label: 'Aucun' },
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
];

const buildInitialForm = () => ({
  selected_days: [],            // [0,1,...]
  ranges: {},                   // { 0: [{start:'10:00', end:'12:00'}] }
  slot_duration_minutes: 60,
  slot_buffer_minutes: 0,
  until: '',                    // optional 'YYYY-MM-DD'
  title: '',
  description: '',
  visibility: 'public',         // forced public per brief
});

// Compute the count of generated slots
const computeSlotCount = (form) => {
  const dur = form.slot_duration_minutes + form.slot_buffer_minutes;
  if (!dur) return 0;
  let total = 0;
  form.selected_days.forEach(d => {
    (form.ranges[d] || []).forEach(r => {
      const [sh, sm] = r.start.split(':').map(Number);
      const [eh, em] = r.end.split(':').map(Number);
      const startMin = sh * 60 + sm;
      const endMin = eh * 60 + em;
      if (endMin > startMin) total += Math.floor((endMin - startMin) / dur);
    });
  });
  return total;
};

const AvailabilityWizard = ({ isOpen, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(buildInitialForm());
  const [submitting, setSubmitting] = useState(false);
  const [createdSchedule, setCreatedSchedule] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setForm(buildInitialForm());
      setCreatedSchedule(null);
    }
  }, [isOpen]);

  const slotCount = useMemo(() => computeSlotCount(form), [form]);

  if (!isOpen) return null;

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleDay = (d) => {
    setForm((f) => {
      const has = f.selected_days.includes(d);
      const selected = has ? f.selected_days.filter(x => x !== d) : [...f.selected_days, d].sort();
      const ranges = { ...f.ranges };
      if (!has && !ranges[d]) ranges[d] = [{ start: '09:00', end: '12:00' }];
      if (has) delete ranges[d];
      return { ...f, selected_days: selected, ranges };
    });
  };

  const updateRange = (day, idx, patch) => {
    setForm((f) => {
      const ranges = { ...f.ranges };
      ranges[day] = (ranges[day] || []).map((r, i) => i === idx ? { ...r, ...patch } : r);
      return { ...f, ranges };
    });
  };

  const addRange = (day) => {
    setForm((f) => {
      const ranges = { ...f.ranges };
      ranges[day] = [...(ranges[day] || []), { start: '14:00', end: '17:00' }];
      return { ...f, ranges };
    });
  };

  const removeRange = (day, idx) => {
    setForm((f) => {
      const ranges = { ...f.ranges };
      ranges[day] = (ranges[day] || []).filter((_, i) => i !== idx);
      if (ranges[day].length === 0) delete ranges[day];
      const selected = ranges[day] ? f.selected_days : f.selected_days.filter(x => x !== day);
      return { ...f, ranges, selected_days: selected };
    });
  };

  const submit = async () => {
    if (form.selected_days.length === 0) {
      toast.error('Sélectionne au moins un jour.');
      setStep(1);
      return;
    }
    if (!form.title.trim()) {
      toast.error('Donne un titre à tes disponibilités.');
      setStep(3);
      return;
    }
    if (slotCount === 0) {
      toast.error('Aucun créneau ne sera généré avec ces réglages.');
      return;
    }

    // Build availability_slots flat array
    const availability_slots = [];
    form.selected_days.forEach(d => {
      (form.ranges[d] || []).forEach(r => {
        availability_slots.push({ day_of_week: d, start_time: r.start, end_time: r.end });
      });
    });

    // We submit as a public_series with extended availability fields.
    // The start_time/end_time fields are kept for backward compat — we use
    // the next occurrence of the first selected day as the "anchor" start.
    const now = new Date();
    const firstDay = form.selected_days[0];
    const firstRange = form.ranges[firstDay][0];
    const daysAhead = (firstDay - ((now.getDay() + 6) % 7) + 7) % 7;  // convert JS Sun=0..Sat=6 to ISO Mon=0..Sun=6
    const anchor = new Date(now);
    anchor.setDate(now.getDate() + (daysAhead || 7));
    const [sh, sm] = firstRange.start.split(':').map(Number);
    const [eh, em] = firstRange.end.split(':').map(Number);
    anchor.setHours(sh, sm, 0, 0);
    const anchorEnd = new Date(anchor);
    anchorEnd.setHours(eh, em, 0, 0);

    setSubmitting(true);
    try {
      const payload = {
        type: 'public_series',
        title: form.title.trim(),
        description: form.description.trim(),
        start_time: anchor.toISOString(),
        end_time: anchorEnd.toISOString(),
        max_capacity: 1,
        is_free: true,
        price: 0,
        currency: 'CHF',
        recurrence: {
          pattern: 'weekly',
          days_of_week: form.selected_days,
          until: form.until ? new Date(`${form.until}T23:59:59`).toISOString() : null,
          occurrences_count: 1,  // ignored when availability_slots present
        },
        series_config: {
          can_book_full_series: false,
          can_book_per_session: true,
        },
        // Brief Jun 16 §3 — new extension fields for availability schedules
        availability_slots,
        slot_duration_minutes: form.slot_duration_minutes,
        slot_buffer_minutes: form.slot_buffer_minutes,
      };
      const { data } = await axios.post(`${API}/calendar/events`, payload, { withCredentials: true });
      setCreatedSchedule(data);
      setStep(4);
      onCreated?.(data);
      toast.success(`Disponibilités publiées — ${slotCount} créneaux générés.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Création impossible.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4" data-testid="availability-wizard-step-1">
      <p className="text-sm text-gray-400">Sélectionne tes jours disponibles et tes plages horaires (récurrence hebdomadaire).</p>
      <div className="flex flex-wrap gap-1.5">
        {DAYS.map((d) => {
          const on = form.selected_days.includes(d.value);
          return (
            <button
              key={d.value}
              type="button"
              data-testid={`availability-wizard-day-${d.value}`}
              onClick={() => toggleDay(d.value)}
              className={`w-12 h-9 rounded-lg text-xs font-medium border transition-all ${
                on ? 'bg-[#1D9E75]/20 border-[#1D9E75]/60 text-[#7fdfbe]' : 'border-white/10 text-gray-400 hover:bg-white/5'
              }`}
            >
              {d.label}
            </button>
          );
        })}
      </div>
      <div className="space-y-3">
        {form.selected_days.map(d => (
          <div key={d} className="p-3 rounded-xl bg-white/5 border border-white/10 space-y-2" data-testid={`availability-wizard-ranges-${d}`}>
            <p className="text-xs font-semibold text-[#7fdfbe]">{DAYS.find(x => x.value === d).long}</p>
            {(form.ranges[d] || []).map((r, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  data-testid={`availability-wizard-range-start-${d}-${idx}`}
                  type="time"
                  value={r.start}
                  onChange={(e) => updateRange(d, idx, { start: e.target.value })}
                  className="px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                />
                <span className="text-xs text-gray-500">→</span>
                <input
                  data-testid={`availability-wizard-range-end-${d}-${idx}`}
                  type="time"
                  value={r.end}
                  onChange={(e) => updateRange(d, idx, { end: e.target.value })}
                  className="px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                />
                {(form.ranges[d] || []).length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRange(d, idx)}
                    className="p-1.5 rounded-lg text-red-300 hover:bg-red-500/10"
                    data-testid={`availability-wizard-range-remove-${d}-${idx}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addRange(d)}
              className="text-xs text-[#7fdfbe] hover:text-[#a4ecd0] inline-flex items-center gap-1"
              data-testid={`availability-wizard-add-range-${d}`}
            >
              <Plus className="h-3 w-3" /> Ajouter une plage
            </button>
          </div>
        ))}
      </div>
      <div className="pt-2">
        <label className="block text-xs font-medium text-gray-300 mb-1">Jusqu&apos;au (optionnel)</label>
        <input
          data-testid="availability-wizard-until"
          type="date"
          value={form.until}
          onChange={(e) => updateForm({ until: e.target.value })}
          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">Laisse vide pour une récurrence hebdomadaire indéfinie.</p>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4" data-testid="availability-wizard-step-2">
      <p className="text-sm text-gray-400">Configure la durée et le tampon de tes créneaux.</p>
      <div>
        <label className="block text-xs font-medium text-gray-300 mb-2">Durée par créneau *</label>
        <div className="flex flex-wrap gap-2">
          {DURATION_OPTIONS.map(o => (
            <button
              key={o.value}
              type="button"
              data-testid={`availability-wizard-duration-${o.value}`}
              onClick={() => updateForm({ slot_duration_minutes: o.value })}
              className={`px-3 py-1.5 rounded-lg text-xs border ${
                form.slot_duration_minutes === o.value
                  ? 'bg-[#1D9E75]/15 border-[#1D9E75]/60 text-[#7fdfbe]'
                  : 'border-white/10 text-gray-400 hover:bg-white/5'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-300 mb-2">Tampon entre créneaux (optionnel)</label>
        <div className="flex flex-wrap gap-2">
          {BUFFER_OPTIONS.map(o => (
            <button
              key={o.value}
              type="button"
              data-testid={`availability-wizard-buffer-${o.value}`}
              onClick={() => updateForm({ slot_buffer_minutes: o.value })}
              className={`px-3 py-1.5 rounded-lg text-xs border ${
                form.slot_buffer_minutes === o.value
                  ? 'bg-[#1D9E75]/15 border-[#1D9E75]/60 text-[#7fdfbe]'
                  : 'border-white/10 text-gray-400 hover:bg-white/5'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="px-3 py-2 rounded-lg bg-[#1D9E75]/10 border border-[#1D9E75]/30 text-xs text-[#7fdfbe]" data-testid="availability-wizard-slot-count">
        <strong>{slotCount}</strong> créneau{slotCount > 1 ? 'x' : ''} ser{slotCount > 1 ? 'ont' : 'a'} généré{slotCount > 1 ? 's' : ''} par semaine
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4" data-testid="availability-wizard-step-3">
      <p className="text-sm text-gray-400">Décris la session proposée dans ces créneaux.</p>
      <div>
        <label className="block text-xs font-medium text-gray-300 mb-1">Titre de la session *</label>
        <input
          data-testid="availability-wizard-title"
          value={form.title}
          onChange={(e) => updateForm({ title: e.target.value })}
          placeholder="Ex : Coaching Gestion de fortune"
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/40 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-300 mb-1">Description (optionnel)</label>
        <textarea
          data-testid="availability-wizard-description"
          value={form.description}
          onChange={(e) => updateForm({ description: e.target.value })}
          rows={2}
          placeholder="Ce que tu vas proposer durant ces créneaux…"
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 text-sm resize-none"
        />
      </div>
      <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-400">
        Visibilité : <strong className="text-[#7fdfbe]">Public</strong> · Prix : <strong className="text-[#7fdfbe]">Gratuit</strong> (soft-launch)
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-5 text-center py-6" data-testid="availability-wizard-step-4">
      <div className="mx-auto w-16 h-16 rounded-full bg-[#1D9E75]/15 border border-[#1D9E75]/40 flex items-center justify-center">
        <Check className="h-8 w-8 text-[#7fdfbe]" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-white">Disponibilités publiées</h3>
        <p className="text-sm text-gray-400 mt-1">{slotCount} créneaux par semaine sont désormais réservables sur ton profil.</p>
      </div>
      {createdSchedule && (
        <div className="mx-auto max-w-md p-4 rounded-xl bg-white/5 border border-white/10 text-left space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-[#7fdfbe]" />
            <span className="text-white font-medium">{createdSchedule.title}</span>
          </div>
          <div className="text-xs text-gray-300">
            {form.selected_days.map(d => DAYS.find(x => x.value === d).label).join(', ')} · {form.slot_duration_minutes} min par créneau
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="availability-wizard-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl bg-gradient-to-br from-[#0d1f35] via-[#0f2540] to-[#0d1f35] border border-white/10 shadow-2xl p-6 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#7fdfbe]" />
            <h2 className="text-lg font-semibold text-white">Mes disponibilités</h2>
          </div>
          <button data-testid="availability-wizard-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-2 mb-6 flex-shrink-0">
          {[1, 2, 3, 4].map((i) => (
            <React.Fragment key={i}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                step === i ? 'bg-[#1D9E75]/20 border border-[#1D9E75]/60 text-[#7fdfbe]'
                : step > i ? 'bg-[#1D9E75]/40 text-white'
                : 'bg-white/5 text-gray-500 border border-white/10'
              }`}>
                {step > i ? <Check className="h-3.5 w-3.5" /> : i}
              </div>
              {i < 4 && <div className="flex-1 h-px bg-white/10" />}
            </React.Fragment>
          ))}
        </div>
        <div className="min-h-[300px] flex-1 overflow-y-auto pr-1">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
          <div>
            {step > 1 && step < 4 && (
              <button data-testid="availability-wizard-back" type="button" onClick={() => setStep(step - 1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Retour
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {step !== 4 && (
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-white/10 text-gray-300 hover:bg-white/5">
                Annuler
              </button>
            )}
            {step < 3 && (
              <button data-testid="availability-wizard-next" type="button"
                onClick={() => {
                  if (step === 1 && form.selected_days.length === 0) {
                    toast.error('Sélectionne au moins un jour.');
                    return;
                  }
                  setStep(step + 1);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-[#1D9E75] text-white font-medium hover:opacity-90">
                Suivant <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {step === 3 && (
              <button data-testid="availability-wizard-submit" type="button" onClick={submit} disabled={submitting}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-[#1D9E75] text-white font-medium hover:opacity-90 disabled:opacity-50">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {submitting ? 'Publication…' : 'Publier mes disponibilités'}
              </button>
            )}
            {step === 4 && (
              <button data-testid="availability-wizard-done" type="button" onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm bg-[#1D9E75] text-white font-medium">
                Terminer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityWizard;
